"use client";

import { useMemo } from "react";
import ArticleContent from "@/components/article/ArticleContent";
import AuthorCard from "@/components/article/AuthorCard";
import SimilarArticles from "@/components/article/SimilarArticles";
import CommentsSection from "@/components/article/CommentsSection";
import RightSidebar from "@/components/layout/RightSidebar";
import ShareRating from "@/components/article/ShareRating";
import { Article } from "@/types";

type ArticlePageClientProps = {
  article: Article;
};

const ArticlePageClient = ({ article }: ArticlePageClientProps) => {
  // Sélection d'une catégorie aléatoire
  const selectedCategory = useMemo(() => {
    if (!article?.categories || article.categories.length === 0) return null;
    if (article.categories.length === 1) return article.categories[0];

    const randomIndex = Math.floor(Math.random() * article.categories.length);
    return article.categories[randomIndex];
  }, [article?.categories]);

  // Vérification si l'article est chargé
  if (!article) {
    return (
      <div className="container mx-auto p-6 text-center text-red-500">
        <p>❌ Erreur : L'article n'a pas pu être chargé.</p>
      </div>
    );
  }

  // Génération dynamique du JSON-LD pour Google
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    author: article.authors?.map((author) => ({
      "@type": "Person",
      name: author.name,
    })),
    datePublished: article.published_date,
    dateModified: article.published_date,
    publisher: {
      "@type": "Organization",
      name: "Les Concernés",
      logo: {
        "@type": "ImageObject",
        url: "https://lesconcernes.com/logo-v2.jpeg",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://lesconcernes.com/article/${article.slug}`,
    },
    image: article.cover_url || "https://lesconcernes.com/logo-v2.jpeg",
  };

  return (
    <>
      <main className="container mx-auto flex flex-col gap-6 p-6 md:flex-row">
        {/* Contenu principal */}
        <div className="space-y-6 md:w-2/3 lg:w-3/4">
          {/* Article */}
          <section>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
            <ArticleContent article={article} />
          </section>

          {/* Partage & Notation */}
          <section>
            <ShareRating />
          </section>

          {/* Carte auteur */}
          {article.authors && article.authors.length > 0 && (
            <section>
              <AuthorCard authors={article.authors} />
            </section>
          )}

          {/* Articles similaires */}
          {selectedCategory ? (
            <section>
              <SimilarArticles slug={selectedCategory.slug} />
            </section>
          ) : (
            <p className="italic text-gray-500">Aucune catégorie associée.</p>
          )}

          {/* Section des commentaires */}
          <section>
            <CommentsSection articleId={article.id} />
          </section>
        </div>

        {/* Sidebar Droite */}
        <RightSidebar />
      </main>
    </>
  );
};

export default ArticlePageClient;
